<template>
	<div class="member-service-page">
		<!-- 顶部 Banner -->
		<div class="vip-banner">
			<div class="banner-content">
				<img src="../assets/image/memberBackground.png" alt="" />
			</div>
		</div>

		<div class="content-wrapper">
			<!-- 用户信息 -->
			<div class="user-info-bar">
				<div class="left">
					<span class="label">开通账号：</span>
					<div class="avatar">
						<img :src="userInfo.avatarImg || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix'"
							alt="avatar" />
					</div>
					<span class="username">{{ userInfo.nickname || '未登录用户' }}</span>
					<a href="javascript:;" class="link" @click="showRightsModal = true">了解会员权益 ></a>
					<span
						v-if="userInfo.memberExpireTime"
						class="member-expire"
					>
						已开通会员，有效期至：{{ userInfo.memberExpireTime }}
					</span>
				</div>
				<div class="right">
					<a href="javascript:;" class="link" @click="openHistoryModal">开通记录 ></a>
				</div>
			</div>

			<!-- 会员权益弹窗 -->
			<div class="modal-overlay" v-if="showRightsModal" @click.self="showRightsModal = false">
				<div class="modal-content rights-modal">
					<div class="modal-header">
						<h3>会员权益</h3>
						<span class="close-btn" @click="showRightsModal = false">&times;</span>
					</div>
					<div class="modal-body">
						<div class="rights-content">
							<div v-html="rightsIntro"></div>
						</div>
					</div>
				</div>
			</div>

			<!-- 开通记录弹窗 -->
			<div class="modal-overlay" v-if="showHistoryModal" @click.self="showHistoryModal = false">
				<div class="modal-content">
					<div class="modal-header">
						<h3>开通记录</h3>
						<span class="close-btn" @click="showHistoryModal = false">&times;</span>
					</div>
					<div class="modal-body">
						<div class="history-list" v-if="historyRecords.length > 0">
							<div class="history-item" v-for="record in historyRecords" :key="record.id">
								<div class="item-icon">
									<!-- SVG Crown Icon -->
									<svg width="24" height="24" viewBox="0 0 24 24" fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<path
											d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
											fill="#ff9900" stroke="#ff9900" stroke-width="2" stroke-linecap="round"
											stroke-linejoin="round" />
										<circle cx="12" cy="12" r="6" fill="white" fill-opacity="0.3" />
									</svg>
								</div>
								<div class="item-details">
									<div class="row-top">
										<span class="item-name">{{ record.name }}</span>
										<span class="item-price">¥{{ record.price }}</span>
									</div>
									<div class="row-bottom">
										<span class="item-date">开通时间：{{ record.date }}</span>
										<span class="item-method">开通方式：{{ record.method }}</span>
									</div>
								</div>
							</div>
						</div>
						<EmptyState v-else description="暂无开通记录" />
					</div>
				</div>
			</div>

			<!-- 会员卡片选择 -->
			<div class="plans-container">
				<div v-for="plan in plans" :key="plan.id" class="plan-card"
					:class="{ active: selectedPlanId === plan.id }" @click="selectPlan(plan)">
					<div class="plan-name">{{ plan.name }}</div>
					<div class="plan-price">
						<span class="currency">¥</span>
						<span class="amount">{{ plan.price }}</span>
						<span class="unit">/{{ plan.unit }}</span>
					</div>
				</div>
			</div>

			<!-- 应付金额 -->
			<div class="payment-amount">
				应付金额：<span class="amount">{{ currentPrice }}元</span>
			</div>

			<!-- 支付方式 -->
			<div class="payment-methods">
				<div class="method-title">请选择支付方式</div>

				<div class="method-item" :class="{ active: paymentMethod === 'wallet' }"
					@click="paymentMethod = 'wallet'">
					<div class="method-left">
						<img class="icon" src="../assets/icon/walletIcon.png" alt="" />
						<span>钱包支付</span>
					</div>
					<div class="radio-circle">
						<div class="inner-circle" v-if="paymentMethod === 'wallet'"></div>
					</div>
				</div>

				<div class="method-item" :class="{ active: paymentMethod === 'wechat' }"
					@click="paymentMethod = 'wechat'">
					<div class="method-left">
						<img class="icon" src="../assets/icon/wechatIcon.png" alt="" />
						<span>微信支付</span>
					</div>
					<div class="radio-circle">
						<div class="inner-circle" v-if="paymentMethod === 'wechat'"></div>
					</div>
				</div>
			</div>

			<!-- 支付按钮 -->
			<button class="pay-btn" @click="handlePay">立即支付</button>
		</div>

		<!-- 微信支付二维码弹窗 -->
		<div class="modal-overlay" v-if="showPayModal" @click.self="showPayModal = false">
			<div class="modal-content pay-modal">
				<div class="modal-header">
					<h3>微信支付</h3>
					<span class="close-btn" @click="showPayModal = false">&times;</span>
				</div>
				<div class="modal-body">
					<transition name="qr-fade" mode="out-in">
						<div class="qr-container" v-if="payQrCode" key="qr">
							<img :src="payQrCode" alt="Payment QR Code" />
							<p>请使用微信扫一扫完成支付</p>
						</div>
						<div class="loading" v-else key="loading">
							<span class="spinner"></span>
							<p>正在生成支付二维码...</p>
						</div>
					</transition>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import MemberAPI from '@/api/member'
	import {
		getUserProfile
	} from '@/api/user'
	import {
		getOrderList,
		createMemberOrder,
		miniAppPay
	} from '@/api/order'
	import {
		getSetting
	} from '@/api/public'
	import EmptyState from '@/components/EmptyState.vue'
	import { ElMessage } from 'element-plus'
	import 'element-plus/es/components/message/style/css'

	export default {
		name: 'MemberService',
		components: {
			EmptyState
		},
		data() {
			return {
				userInfo: {
					nickName: '未登录用户',
					avatar: ''
				},
				rightsIntro: '成为会员，您将立即获得平台完整视频库的无限制访问权。这不仅仅是一个"视频合集"，而是一个持续更新、精心编排的体系化知识生态系统。从行业大师的独家课程、前沿领域的深度解析，到实用技能的步步拆解，覆盖职场、技术、创意、生活等上百个维度。您的每一个"我想学"，在这里都有清晰的回响。',
				showRightsModal: false,
				showHistoryModal: false,
				historyRecords: [],
				selectedPlanId: null,
				paymentMethod: 'wallet', // wallet, wechat
				plans: [],
				showPayModal: false,
				payQrCode: '',
				loading: false
			}
		},
		computed: {
			currentPrice() {
				const plan = this.plans.find(p => p.id === this.selectedPlanId)
				return plan ? plan.price : 0
			}
		},
		mounted() {
			this.fetchMemberTypes()
			this.fetchUserProfile()
			this.fetchVipSettings()
		},
		methods: {
			async handlePay() {
				if (!this.selectedPlanId) {
					ElMessage.warning('请选择会员套餐')
					return
				}

				try {
					this.loading = true
					// 1. 创建会员订单
					const createRes = await createMemberOrder({
						memberTypeId: this.selectedPlanId
					})

					let ordersId= createRes.orderId
					

					console.log('Created order, ordersId:', ordersId)

					if (!ordersId) {
						throw new Error('订单创建失败，未获取到订单ID')
					}

					// 2. 发起支付（微信支付使用 payType = 3）
					const payType = this.paymentMethod === 'wechat' ? 3 : 2
					const payRes = await miniAppPay({
						ordersId: ordersId,
						payType: payType
					})

					console.log('Payment response:', payRes)

					// 处理支付结果
					if (this.paymentMethod === 'wallet') {
						ElMessage.success('支付成功！')
						// 刷新用户信息和记录
						this.fetchUserProfile()
						this.fetchOrderHistory()
					} else {
						// 微信支付：展示二维码弹窗，逻辑参考 MyWallet.vue
						this.showPayModal = true
						this.payQrCode = ''

						if (payRes && (payRes.codeUrl || payRes.code_url)) {
							const codeUrl = payRes.codeUrl || payRes.code_url
							this.payQrCode =
								`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(codeUrl)}`
						} else if (typeof payRes === 'string' && payRes.startsWith('weixin://')) {
							this.payQrCode =
								`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(payRes)}`
						} else {
							// 兜底：如果 payRes 是 base64 或图片链接
							this.payQrCode = (payRes && (payRes.codeUrl || payRes.qrCode)) || payRes || ''
						}

						if (!this.payQrCode) {
							ElMessage.warning('未获取到支付二维码，请稍后重试')
							this.showPayModal = false
						}
					}
				} catch (error) {
					console.error('购买失败:', error)
					ElMessage.error(error.message || '购买失败，请重试')
				} finally {
					this.loading = false
				}
			},
			openHistoryModal() {
				this.showHistoryModal = true
				this.fetchOrderHistory()
			},
			async fetchOrderHistory() {
				try {
					const res = await getOrderList({
						goodsType: 2,
						payStatus: 1
					})
					let list = []
					if (res && res.rows) {
						list = res.rows
					} else if (res && res.data) {
						list = res.data
					} else if (Array.isArray(res)) {
						list = res
					} else if (res && res.list) {
						list = res.list
					}

					this.historyRecords = list.map(item => ({
						id: item.orderId || item.id,
						name: item.goodsName || item.productName || '会员服务',
						price: item.amount ,
						date: item.payTime || item.createTime,
						method: this.formatPayType(item.payType)
					}))
				} catch (error) {
					console.error('获取开通记录失败:', error)
				}
			},
			formatPayType(type) {
				// 兼容字符串和数值两种支付类型标识，以及旧/新编号
				if (type == 'wechat' || type == 3) return '微信支付'
				// if (type == 'wallet' || type == 2) return '钱包支付'
				if (type == 'alipay' || type == 1) return '钱包支付'
				return '其他支付'
			},
			async fetchUserProfile() {
				try {
					const res = await getUserProfile()
					if (res) {
						this.userInfo = res
					}
				} catch (error) {
					console.error('获取用户信息失败:', error)
				}
			},
			async fetchVipSettings() {
				try {
					const res = await getSetting({
						groupKey: 'vip'
					})
					if (res && res.vip) {
						this.rightsIntro = res.vip
					}
				} catch (error) {
					console.error('获取会员权益设置失败:', error)
				}
			},
			async fetchMemberTypes() {
				try {
					const res = await MemberAPI.getMemberTypes()
					// 兼容直接返回数组或对象包含 list 的情况
					let list = []
					if (Array.isArray(res)) {
						list = res
					} else if (res && Array.isArray(res.data)) {
						list = res.data
					} else if (res && Array.isArray(res.list)) {
						list = res.list
					}

					console.log('Member types list:', list)

					if (list && list.length > 0) {
						this.plans = list.map(item => ({
							id: item.memberTypeId || item.id, // 兼容字段名
							name: item.name,
							price: item.price,
							// 如果接口没有返回 unit，尝试从名称中解析，或者根据天数判断
							unit: item.unit || this.guessUnit(item.name, item.days)
						}))
						// 默认选中第一个
						if (!this.selectedPlanId && this.plans.length > 0) {
							this.selectedPlanId = this.plans[0].id
						}
					} else {
						// 如果没有数据，使用默认数据
						console.warn('No member types data received')
					}
				} catch (error) {
					console.error('获取会员套餐失败:', error)
					// 如果失败，可以使用默认数据兜底，或者显示错误提示
					this.selectedPlanId = 1
				}
			},
			guessUnit(name, days) {
				if (name && name.includes('日')) return '日'
				if (name && name.includes('月')) return '月'
				if (name && name.includes('季')) return '季'
				if (name && name.includes('年')) return '年'
				if (days) {
					if (days < 30) return '日'
					if (days < 90) return '月'
					if (days < 365) return '季'
					return '年'
				}
				return '期'
			},
			selectPlan(plan) {
				this.selectedPlanId = plan.id
			}
		}
	}
</script>

<style lang="scss" scoped>
	.member-service-page {
		background-color: #111;
		min-height: 100vh;
		color: #fff;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		padding-bottom: 50px;
	}

	.vip-banner {
		height: 154px;
		width: 100%;
		display: flex;
		justify-content: center;
		color: #5c4013;

		.banner-content {
			width: 100%;
			max-width: 1200px;
			height: 100%;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
	}

	.content-wrapper {
		max-width: 1200px;
		margin: 0 auto;
		background: #141319;
		padding: 20px;
	}

	.user-info-bar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin: 30px 0;
		font-size: 14px;
		color: #999;

		.left {
			display: flex;
			align-items: center;
			gap: 10px;

			.avatar {
				width: 30px;
				height: 30px;
				border-radius: 50%;
				overflow: hidden;
				background: #ccc;

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			.username {
				color: #fff;
				font-weight: 500;
			}

			.member-expire {
				margin-left: 16px;
				color: #e6c88b;
				font-size: 12px;
				white-space: nowrap;
			}
		}

		.link {
			color: #999;
			text-decoration: none;
			margin-left: 10px;
			font-size: 12px;

			&:hover {
				color: #fff;
			}
		}
	}

	.plans-container {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 20px;
		margin-bottom: 40px;
	}

	.plan-card {
		background: #262626;
		border: 2px solid transparent;
		border-radius: 12px;
		padding: 40px 20px;
		text-align: center;
		cursor: pointer;
		transition: all 0.3s ease;
		height: 200px;
		display: flex;
		flex-direction: column;
		justify-content: center;

		.plan-name {
			font-size: 18px;
			font-weight: bold;
			margin-bottom: 30px;
			color: #ccc;
			position: relative;

			&::after {
				content: '';
				position: absolute;
				bottom: -15px;
				left: 50%;
				transform: translateX(-50%);
				width: 30px;
				height: 2px;
				background-color: #E4A349;
			}
		}

		.plan-price {
			color: #e6c88b;
			display: flex;
			align-items: baseline;
			justify-content: center;
			margin-top: 10px;

			.currency {
				font-size: 20px;
				vertical-align: top;
				margin-right: 4px;
				font-weight: bold;
			}

			.amount {
				font-size: 40px;
				font-weight: bold;
				line-height: 1;
			}

			.unit {
				font-size: 14px;
				color: #999;
				margin-left: 2px;
			}
		}

		&:hover {
			transform: translateY(-2px);
			border-color: #E4A349;
		}

		&.active {
			background: linear-gradient(0deg, #FDE7AC 0%, #FEF4DB 100%);
			border-color: #FDF3D6;

			.plan-name {
				color: #583608;

				&::after {
					background-color: #583608;
				}
			}

			.plan-price {
				color: #583608;

				.unit {
					color: #583608;
					opacity: 0.8;
				}
			}
		}
	}

	.payment-amount {
		text-align: center;
		font-size: 16px;
		margin-bottom: 30px;

		.amount {
			color: #e6c88b;
			font-size: 24px;
			font-weight: bold;
			margin-left: 5px;
		}
	}

	.payment-methods {
		width: 100%;
		margin: 0 auto 40px;

		.method-title {
			font-size: 14px;
			color: #ccc;
			margin-bottom: 15px;
		}

		.method-item {
			background: #1f1f1f;
			border: 1px solid #333;
			border-radius: 4px;
			padding: 20px 25px;
			margin-bottom: 20px;
			display: flex;
			justify-content: space-between;
			align-items: center;
			cursor: pointer;
			transition: all 0.3s;

			&:hover {
				border-color: #666;
			}

			&.active {
				border-color: #FE8E04;
				background: #2a2a2a;
			}

			.method-left {
				display: flex;
				align-items: center;
				font-size: 16px;
				color: #fff;

				.icon {
					width: 30px;
					height: 30px;
					margin-right: 15px;
					font-size: 24px;
					display: flex;
					align-items: center;
				}


			}

			.radio-circle {
				width: 20px;
				height: 20px;
				border-radius: 50%;
				border: 2px solid #666;
				display: flex;
				align-items: center;
				justify-content: center;
				position: relative;

				.inner-circle {
					width: 12px;
					height: 12px;
					border-radius: 50%;
					background: #FE8E04;
				}
			}

			&.active .radio-circle {
				border-color: #FE8E04;
			}
		}
	}

	.pay-btn {
		display: block;
		width: 502px;
		height: 62px;
		background: #FE8E04;
		border-radius: 16px 16px 16px 16px;
		margin: 0 auto;
		color: #fff;
		border: none;
		height: 50px;
		font-size: 16px;
		font-weight: bold;
		cursor: pointer;
		transition: background 0.3s;


	}

	/* Modal Styles */
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.7);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000;
	}

	.modal-content {
		background-color: #fff;
		width: 600px;
		max-width: 90%;
		border-radius: 12px;
		padding: 20px;
		color: #333;
		position: relative;
		animation: modalFadeIn 0.3s ease;
	}

	@keyframes modalFadeIn {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}

		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.modal-header {
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		margin-bottom: 20px;
		padding-bottom: 10px;

		h3 {
			font-size: 18px;
			font-weight: bold;
			color: #333;
			margin: 0;
		}

		.close-btn {
			position: absolute;
			right: 0;
			top: 0;
			font-size: 24px;
			color: #999;
			cursor: pointer;
			line-height: 1;

			&:hover {
				color: #333;
			}
		}
	}

	.history-list {
		max-height: 400px;
		overflow-y: auto;
		padding-right: 5px;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-thumb {
			background-color: #ccc;
			border-radius: 3px;
		}
	}

	.history-item {
		display: flex;
		align-items: center;
		padding: 15px;
		border: 1px solid #f0f0f0;
		border-radius: 8px;
		margin-bottom: 15px;
		transition: all 0.2s;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

		&:hover {
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
			border-color: #e6e6e6;
		}

		.item-icon {
			width: 40px;
			height: 40px;
			border-radius: 50%;
			background-color: #FFF8E5;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-right: 15px;
			flex-shrink: 0;

			svg {
				width: 20px;
				height: 20px;
			}
		}

		.item-details {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 8px;

			.row-top {
				display: flex;
				justify-content: space-between;
				align-items: center;

				.item-name {
					font-size: 16px;
					font-weight: 500;
					color: #333;
				}

				.item-price {
					font-size: 18px;
					font-weight: bold;
					color: #ff9900;
				}
			}

			.row-bottom {
				display: flex;
				justify-content: space-between;
				align-items: center;
				font-size: 12px;
				color: #999;
			}
		}
	}

	.rights-content {
		padding: 20px 40px;
		font-size: 14px;
		line-height: 1.8;
		color: #666;
		text-align: justify;

		p {
			margin: 0;
			text-indent: 2em;
		}
	}

	/* 微信支付弹窗样式（参考 MyWallet.vue） */
	.pay-modal {
		width: 400px;
		border-radius: 8px;
		padding: 20px;
		color: #333;
	}

	.qr-container {
		display: flex;
		flex-direction: column;
		align-items: center;

		img {
			width: 200px;
			height: 200px;
			margin-bottom: 15px;
		}

		p {
			color: #666;
			font-size: 14px;
		}
	}

	.loading {
		text-align: center;
		padding: 40px 0;
		color: #666;

		.spinner {
			display: inline-block;
			width: 24px;
			height: 24px;
			border: 3px solid rgba(0, 0, 0, 0.1);
			border-top-color: #409eff;
			border-radius: 50%;
			animation: spinner-rotate 0.8s linear infinite;
			margin-bottom: 10px;
		}

		p {
			margin: 0;
		}
	}

	/* 二维码生成 / 显示动画 */
	.qr-fade-enter-active,
	.qr-fade-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.qr-fade-enter-from,
	.qr-fade-leave-to {
		opacity: 0;
		transform: scale(0.9);
	}

	.qr-fade-enter-to,
	.qr-fade-leave-from {
		opacity: 1;
		transform: scale(1);
	}

	@keyframes spinner-rotate {
		from {
			transform: rotate(0deg);
		}

		to {
			transform: rotate(360deg);
		}
	}
</style>